import React, { useState, useEffect } from "react";
import { Sparkles, Brain, Zap } from "lucide-react";
import { CampaignInfo, PostContent, GeneratedPost, Platform } from "../types";
import { generateAllPosts } from "../lib/gemini";
import {
  getPlatformIcon,
  getPlatformDisplayName,
  getPlatformColors,
} from "../utils/platformIcons";
import { useTranslation } from "react-i18next";
import { useNavigationGuard } from "../hooks/useNavigationGuard";
import { useUser } from "@/store/useUser";
import { notify } from "@/utils/toast";
import { useAppContext } from "@/context/AppContext";
import { useDraftStoreZustand } from "@/store/useDraftStore";

interface AIGeneratorProps {
  contentData: any;
  onComplete: (posts: any[]) => void;
  onBack?: () => void;
}

export const AIGenerator: React.FC<AIGeneratorProps> = ({
  contentData,
  onComplete,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const { t } = useTranslation();
  const { user } = useUser();

  useNavigationGuard({
    isActive: isGenerating,
    title: t("confirm_navigation") || "Confirm Navigation",
    message:
      t("publishing_in_progress") ||
      "Post generation in progress. Are you sure you want to leave?",
  });

  const useRefFlag = React.useRef(false);
  useEffect(() => {
    if (useRefFlag.current) return;
    useRefFlag.current = true;
    if (contentData) {
      generatePosts();
    }
  }, [contentData]);

  const targetPlatforms: Platform[] = contentData?.selectedPlatforms ||
    contentData?.platforms || ["linkedin"];
  const { saveOrUpdateDraft } = useDraftStoreZustand();

  const generatePosts = async () => {
    if (isGenerating) return;

    setIsGenerating(true);
    setProgress(0);

    try {
      const campaignInfo: CampaignInfo = {
        name:
          contentData?.campaignName || contentData?.campaignInfo?.name || "",
        website:
          contentData?.website || contentData?.campaignInfo?.website || "",
        industry:
          contentData?.industry ||
          contentData?.campaignInfo?.industry ||
          t("general"),
        description:
          contentData?.description ||
          contentData?.campaignInfo?.description ||
          t("general_content_generation"),
        targetAudience:
          contentData?.targetAudience ||
          contentData?.campaignInfo?.target_audience ||
          contentData?.campaignInfo?.targetAudience ||
          t("general_audience"),
        brandTone:
          contentData?.tone ||
          contentData?.campaignInfo?.brand_tone ||
          (contentData?.campaignInfo?.brandTone as any) ||
          "professional",
        goals: contentData?.goals ||
          contentData?.campaignInfo?.goals || ["brand_building"],
        platforms: targetPlatforms,
        objective:
          contentData?.objective || contentData?.campaignInfo?.objective,
        keywords:
          contentData?.keywords || contentData?.campaignInfo?.keywords || [],
        hashtags:
          contentData?.hashtags || contentData?.campaignInfo?.hashtags || [],
      };

      const progressInterval = setInterval(() => {
        setProgress((prev) => (prev < 85 ? prev + 5 : prev));
      }, 400);

      const posts = await generateAllPosts(
        user,
        campaignInfo,
        { ...contentData, prompt: `${contentData?.basePrompt}`.trim() },
        (_platform, p) => setProgress(p)
      );

      clearInterval(progressInterval);
      setProgress(100);
      setIsGenerating(false);

      if (posts && posts.length > 0) {
        posts.forEach((post: any) => {
          const payloadData = {
            postId: post.postId,
            status: "draft",
            postContent: post.content || post.caption || "",
            youtubeTitle: post.youtubeTitle || "",
            youtubeDescription: post.youtubeDescription || "",
            tiktok_title: post.tiktok_title || "",
            tiktok_description: post.tiktok_description || "",
            hashtag:
              post.hashtags?.toString() ||
              contentData?.hashtags?.toString() ||
              "",
            prompt: post.generationPrompt || contentData?.prompt || "",
            publishedUrls: {},
            platforms: [post.platform],
            category: "Draft",
            imageUrl: post.imageUrl || post.mediaUrl || null,
          };
          saveOrUpdateDraft(post.platform, payloadData);
        });

        onComplete(posts);
      }
    } catch (error: any) {
      console.error("Error generating posts:", error);
      notify("error", error.message || "Failed to generate posts");
      setIsGenerating(false);

      if (error.message?.includes("quota")) {
        const fallbackPosts = targetPlatforms.map((platform: Platform) => ({
          platform,
          caption: contentData?.prompt || "Check out our latest updates!",
          hashtags: ["#business", "#updates"],
          imageUrl: null,
        }));
        onComplete(fallbackPosts);
      } else {
        onComplete([]);
      }
    }
  };

  return (
    <div className="max-w-full mx-auto bg-white md:border md:border-gray-100 md:rounded-md md:shadow-md md:m-6 h-fit md:p-4 py-4">
      <div className="text-center mb-8">
        <div className="md:w-12 md:h-12 w-10 h-10 aspect-square bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
          <Brain className="md:w-8 w-6 md:h-8 h-6 text-blue-600" />
        </div>
        <h2 className="md:text-3xl text-2xl font-bold text-slate-900 mb-2">
          {t("ai_crafting_posts")}
        </h2>
        <p className="text-gray-500 font-medium">
          {t("creating_optimized_content")}
        </p>
      </div>

      <div className="space-y-6">
        {/* Progress Bar */}
        <div className="bg-gray-100 rounded-full h-3 overflow-hidden">
          <div
            className="bg-gradient-to-r from-[#1666fb] to-purple-500 h-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* All platforms generating at once */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-md border border-blue-200">
          <p className="text-sm font-medium text-center text-slate-600 mb-3">
            Generating all platforms simultaneously...
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {targetPlatforms.map((platform, i) => {
              const IconComponent = getPlatformIcon(platform);
              return (
                <div
                  key={platform}
                  className="            relative rounded-full
            flex items-center justify-center
            transition-all duration-200
            w-10 h-10 md:w-12 md:h-12 bg-blue-50 0 shadow-lg scale-105"
                  style={{ animationDelay: `${i * 0.15}s` }}
                >
                  <div
                  >
                    {IconComponent ? (
                      <IconComponent className="                w-6 h-6 md:w-7 md:h-7 transition-colors
" />
                    ) : (
                      <Brain className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                                        <div className="absolute inset-0 rounded-full border border-blue-500 animate-pulse"></div>

             
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 p-4 rounded-md">
            <Sparkles className="w-6 h-6 text-blue-600 mb-2" />
            <h3 className="font-medium text-slate-900">
              {t("smart_optimization")}
            </h3>
            <p className="text-sm text-gray-500 font-medium">
              {t("tailoring_content")}
            </p>
          </div>
          <div className="bg-white border border-gray-200 p-4 rounded-md">
            <Zap className="w-6 h-6 text-blue-600 mb-2" />
            <h3 className="font-medium text-slate-900">
              {t("hashtag_research")}
            </h3>
            <p className="text-sm text-gray-500 font-medium">
              {t("finding_hashtags")}
            </p>
          </div>
          <div className="bg-white border border-gray-200 p-4 rounded-md">
            <Brain className="w-6 h-6 text-blue-600 mb-2" />
            <h3 className="font-medium text-slate-900">{t("tone_analysis")}</h3>
            <p className="text-sm text-gray-500 font-medium">
              {t("matching_brand_voice")}
            </p>
          </div>
        </div>

        <div className="text-center text-sm text-gray-500 font-medium">
          <p>
            {t("processing1")} {targetPlatforms.length} {t("platforms")}
            {targetPlatforms.length > 1 ? "s" : ""}...
          </p>
        </div>
      </div>
    </div>
  );
};
